// Commission agencies slice.
//
// One normalized list plus a per-operation write state for create and update,
// all from the shared factories. `list.byGuid` is the agency cache the detail
// page reads, so a list or detail fetch fills it and the view needs no refetch.

import * as types from '../../lib/actionTypes.es6';
import {
  createListReducer,
  createWriteReducer,
  initialListState,
  initialWriteState
} from '../../components/CommissionsShared/listReducerFactory.es6';

var agenciesList = createListReducer(
  {
    request: types.GET_COMMISSION_AGENCIES_REQUEST,
    success: types.GET_COMMISSION_AGENCIES_SUCCESS,
    failure: types.GET_COMMISSION_AGENCIES_FAILURE,
    detailSuccess: types.GET_COMMISSION_AGENCY_SUCCESS
  },
  // Keep the current page visible while the next one loads, so changing a
  // filter does not blank the table mid-read.
  { keepRowsWhileLoading: true }
);

var WRITES = {
  create: {
    request: types.CREATE_COMMISSION_AGENCY_REQUEST,
    success: types.CREATE_COMMISSION_AGENCY_SUCCESS,
    failure: types.CREATE_COMMISSION_AGENCY_FAILURE,
    reset: types.RESET_CREATE_COMMISSION_AGENCY_SUCCESS
  },
  update: {
    request: types.UPDATE_COMMISSION_AGENCY_REQUEST,
    success: types.UPDATE_COMMISSION_AGENCY_SUCCESS,
    failure: types.UPDATE_COMMISSION_AGENCY_FAILURE,
    reset: types.RESET_UPDATE_COMMISSION_AGENCY_SUCCESS
  }
};

var writeReducers = {};
Object.keys(WRITES).forEach(function(name) {
  writeReducers[name] = createWriteReducer(WRITES[name]);
});

var ERROR_RESETS = {
  create: types.RESET_CREATE_COMMISSION_AGENCY_ERROR,
  update: types.RESET_UPDATE_COMMISSION_AGENCY_ERROR
};

export function initialState() {
  var state = {
    list: initialListState(),

    // The params of the last list request, so Retry and the post-write refresh
    // reuse the filters the user actually had applied.
    listParams: {},

    // The agency open in the detail page; the record itself lives in
    // list.byGuid.
    detailLoading: false,
    detailError: null
  };
  Object.keys(writeReducers).forEach(function(name) {
    state[name] = initialWriteState();
  });
  return state;
}

export default function commissionAgenciesReducer(state, action) {
  if (state === undefined) state = initialState();
  if (!action || !action.type) return state;

  var next = state;

  var list = agenciesList(state.list, action);
  if (list !== state.list) {
    next = Object.assign({}, next, { list: list });
  }

  Object.keys(writeReducers).forEach(function(name) {
    var before = next[name];
    var after = writeReducers[name](before, action);
    if (after !== before) {
      var patch = {};
      patch[name] = after;
      next = Object.assign({}, next, patch);
    }
  });

  switch (action.type) {
    case types.GET_COMMISSION_AGENCIES_REQUEST:
      return Object.assign({}, next, { listParams: action.payload || {} });

    case types.GET_COMMISSION_AGENCY_REQUEST:
      return Object.assign({}, next, {
        detailLoading: true,
        detailError: null
      });

    case types.GET_COMMISSION_AGENCY_SUCCESS:
      return Object.assign({}, next, {
        detailLoading: false,
        detailError: null
      });

    case types.GET_COMMISSION_AGENCY_FAILURE:
      return Object.assign({}, next, {
        detailLoading: false,
        detailError:
          action.payload || { message: 'Could not load this agency' }
      });

    default:
      break;
  }

  var names = Object.keys(ERROR_RESETS);
  for (var i = 0; i < names.length; i += 1) {
    if (action.type === ERROR_RESETS[names[i]]) {
      var cleared = {};
      cleared[names[i]] = Object.assign({}, next[names[i]], { error: null });
      return Object.assign({}, next, cleared);
    }
  }

  return next;
}
